
import {
  defineComponent,
  ref,
  computed,
  watch,
  toRef,
  PropType,
  nextTick,
} from "vue";
import { hideModal } from "@/core/helpers/dom";
import { Form, Field, ErrorMessage } from "vee-validate";
import { TranslationService } from "@/core/services/TranslationService";
import VZMultiSelect from "./VZMultiSelect.vue";
import AddAddressModal from "./AddAddressModal.vue";
import {
  IAddEditFormModel,
  IFormFieldModel,
} from "@/interfaces/iAddEditFormModel";
import moment from "moment-timezone";
import Datepicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import { FormatMethods } from "@/helpers/data-format";
// import { SiteApi } from "@/services/api/Site.service";

export default defineComponent({
  name: "add-edit-modal",
  props: {
    data: { type: Object as PropType<IAddEditFormModel>, default: null },
    viewName: { type: String, default: "" },
    modelValue: { type: Boolean, default: false },
    appendToBody: { type: Boolean, default: false },
  },
  emits: ["on-close", "on-site-change", "on-submit", "on-cancel", "close"],
  components: {
    Form,
    Field,
    ErrorMessage,
    VZMultiSelect,
    Datepicker,
    AddAddressModal,
  },
  setup(props, { emit }) {
    const viewNameObj = toRef(props, "viewName");
    const dataProp = toRef(props, "data");
    const modelViewValue = toRef(props, "modelValue");
    const addressData = ref<any>({});
    const formRef = ref<null | HTMLFormElement>(null);
    const formScroll = ref<null | HTMLFormElement>(null);
    const newTargetModalRef = ref<null | HTMLElement>(null);
    const loading = ref<boolean>(false);
    const isEditMode = ref<boolean>(true);
    const addAddressField = ref<boolean>(true);
    const validationOnUserEmail = ref<boolean>(false);
    const addressVal = ref<string>("");
    const showAddressModal = ref<boolean>(false);
    // const countryOptions = ref<Array<{ label: any; value: string }>>([]);
    let t = function (key: string) {
      return TranslationService.translate(key);
    };
    const cloneDeep = (payload: any) => {
      try {
        return JSON.parse(JSON.stringify(payload ?? {}));
      } catch (e) {
        // Fallback to shallow copy if serialization fails
        return { ...(payload ?? {}) };
      }
    };

    let formData = ref(
      computed(() => {
        return props.data;
      }).value
    );
    const headerLabel = computed(() => {
      if (dataProp.value.label) {
        const label = dataProp.value.edit ? "editHeader" : "addHeader";
        const headerKey = dataProp.value.label.find((item) => item[label]);
        if (headerKey) {
          return headerKey[label];
        }
      }
      const preFix = `${
        dataProp.value.edit ? t("common.edit") : t("common.addNew")
      }`;
      if (dataProp.value.overrideLable) {
        return `${formData.value.header}`;
      }
      return `${preFix} ${formData.value.header}`;
    });
    const saveBtnLabel = computed(() => {
      if (dataProp.value.label) {
        const label = "saveBtn";
        const headerKey = dataProp.value.label.find((item) => item[label]);
        if (headerKey) {
          return headerKey[label];
        }
      }
      return `${t("common.save")}`;
    });
    const isMulitSelectField = computed(() => {
      return (
        formData?.value?.model?.findIndex((item) => item?.multiSelect) > -1
      );
    });
    const isHeadQuarterStaff = (value) => {
      // Check if value and its nested properties exist
      if (
        !value ||
        !value.PermssionChecked ||
        !value.PermssionChecked.userRoleModels ||
        !value.PermssionChecked.userRoleModels[0]
      ) {
        return false;
      }
      return (
        value.PermssionChecked.userRoleModels[0].name ===
          "Head Quarter Staff" ||
        value.PermssionChecked.userRoleModels[0].name === "Client Admin"
      );
    };

    const displayDate = computed(() => {
      return process.env.VUE_APP_DATE_PICKER_FORMAT || "MM-dd-yyyy";
    });
    const maskForElement = (value) => {
      return value === "macAddress" ? "XX:XX:XX:XX:XX:XX" : "";
    };

    const updateShowModalVal = (val) => {
      showAddressModal.value = val;
    };

    const minDate = computed(() => {
      return moment().format("YYYY-MM-DD");
    });
    const minEndDate = computed(() => {
      const today = moment().format("YYYY-MM-DD");
      const startDate = moment(targetData.value.startDate).format("YYYY-MM-DD");
      if (today >= startDate) {
        return today;
      } else {
        return startDate;
      }
    });

    const discard = () => {
      targetData.value = {};
      addAddressField.value = true;
      operationalHoursEnabled.value = false;
      var appElement = document.getElementById("app");
      if (appElement) {
        appElement.click();
      }
      hideModal(newTargetModalRef.value);
      formRef?.value?.resetForm();
      emit("on-cancel", null);
    };

    const showAddModal = (data, editable) => {
      // getCountries();
      // Always use the latest addressModel if present and non-empty, otherwise fallback to dataProp.data
      if (
        targetData.value["addressModel"] &&
        Object.keys(targetData.value["addressModel"]).length > 0
      ) {
        addressData.value = { ...targetData.value["addressModel"] };
      } else if (
        dataProp.value.data &&
        Object.keys(dataProp.value.data).length > 0
      ) {
        addressData.value = { ...dataProp.value.data };
      } else {
        addressData.value = {};
      }
      isEditMode.value = !!editable;
      showAddressModal.value = true;
    };

    // const getCountries = async () => {
    //   countryOptions.value = [];
    //   await SiteApi.getInstance()
    //     .getCountries()
    //     .then((data) => {
    //       data.map((item) => {
    //         countryOptions.value.push({
    //           label: item.text,
    //           value: item.value,
    //         });
    //       });
    //     });
    // };
    const addressSubmit = (address) => {
      addressVal.value = address;
      // targetData.value.addressModel = {};
      targetData.value["address"] =
        address["street"] +
        ", " +
        address["street2"] +
        "" +
        (address["street2"] == "" ? "" : ", ") +
        address["city"] +
        ", " +
        address["state"] +
        ", " +
        address["postalCode"] +
        ", " +
        address["country"];

      targetData.value["addressModel"] = { ...address };
    };

    let targetData = ref<any>({});
    const operationalHoursEnabled = ref<boolean>(false);
    const operationalHoursValidationError = ref<string | null>(null);

    const getOperationalHoursOpenError = (dayValue: string) => {
      const op = targetData.value.operationHours;
      if (!op || !op.week) return null;
      const d = op.week[dayValue];
      if (!d || d.closed) return null;
      if (!d.open) {
        return t("pages.site.startTimeRequired");
      }
      return null;
    };

    const getOperationalHoursCloseError = (dayValue: string) => {
      const op = targetData.value.operationHours;
      if (!op || !op.week) return null;
      const d = op.week[dayValue];
      if (!d || d.closed) return null;
      if (!d.close) {
        return t("pages.site.endTimeRequired");
      }
      return null;
    };
    const daysOfWeek = [
      { label: "Monday", value: "mon" },
      { label: "Tuesday", value: "tue" },
      { label: "Wednesday", value: "wed" },
      { label: "Thursday", value: "thu" },
      { label: "Friday", value: "fri" },
      { label: "Saturday", value: "sat" },
      { label: "Sunday", value: "sun" },
    ];
    const onClosedChange = (dayValue, fieldName = "operationHours") => {
      const week = targetData.value[fieldName]?.week;
      if (week && week[dayValue]) {
        if (week[dayValue].closed) {
          delete week[dayValue].open;
          delete week[dayValue].close;
        } else if (!week[dayValue].open || !week[dayValue].close) {
          // Default if not set
          week[dayValue].open = "00:00";
          week[dayValue].close = "23:59";
        }
      }
    };
    const initializeOperationalHours = () => {
      if (!targetData.value.operationHours) {
        targetData.value.operationHours = { week: {} };
      }
      if (!targetData.value.operationHours.week) {
        targetData.value.operationHours.week = {};
      }
      daysOfWeek.forEach((day) => {
        if (!targetData.value.operationHours.week[day.value]) {
          targetData.value.operationHours.week[day.value] = {
            closed: false,
            open: "00:00",
            close: "23:59",
          };
        } else {
          // Ensure closed is boolean
          targetData.value.operationHours.week[day.value].closed =
            !!targetData.value.operationHours.week[day.value].closed;
          // Clean up if closed but has times
          if (targetData.value.operationHours.week[day.value].closed) {
            delete targetData.value.operationHours.week[day.value].open;
            delete targetData.value.operationHours.week[day.value].close;
          } else if (
            !targetData.value.operationHours.week[day.value].open ||
            !targetData.value.operationHours.week[day.value].close
          ) {
            targetData.value.operationHours.week[day.value].open = "00:00";
            targetData.value.operationHours.week[day.value].close = "23:59";
          }
        }
      });
      operationalHoursEnabled.value = true;
    };

    const toggleOperationalHours = () => {
      operationalHoursEnabled.value = !operationalHoursEnabled.value;
      operationalHoursValidationError.value = null;
      if (operationalHoursEnabled.value) {
        initializeOperationalHours();
      } else {
        targetData.value.operationHours = null;
      }
    };
    const submit = (values, { resetForm }) => {
      loading.value = true;

      if (viewNameObj.value === "sites" && operationalHoursEnabled.value) {
        const op = targetData.value.operationHours;
        if (op && op.week) {
          const invalidDay = daysOfWeek.find((day) => {
            const d = op.week[day.value];
            if (!d || d.closed) {
              return false;
            }

            return !d.open || !d.close;
          });

          if (invalidDay) {
            loading.value = false;
            return false;
          }
        }
      }

      const isCheckbox2Unchecked = !addAddressField.value;
      const isText2Empty = !targetData.value.userEmail;

      if (isCheckbox2Unchecked && isText2Empty) {
        validationOnUserEmail.value = true;
        loading.value = false;
        return false;
      } else {
        validationOnUserEmail.value = false;
      }
      const addr = values.address;
      targetData.value.addressModel = {};
      if (typeof addr === "string" && addr.length > 5) {
        const fullAddress = addr.split(", ");
        if (fullAddress.length == 6) {
          targetData.value.addressModel.street = fullAddress[0];
          targetData.value.addressModel.street2 = fullAddress[1];
          targetData.value.addressModel.city = fullAddress[2];
          targetData.value.addressModel.state = fullAddress[3];
          targetData.value.addressModel.postalCode = fullAddress[4];
          targetData.value.addressModel.country = fullAddress[5];
        } else {
          targetData.value.addressModel.street = fullAddress[0];
          targetData.value.addressModel.street2 = "";
          targetData.value.addressModel.city = fullAddress[1];
          targetData.value.addressModel.state = fullAddress[2];
          targetData.value.addressModel.postalCode = fullAddress[3];
          targetData.value.addressModel.country = fullAddress[4];
        }
      }

      const dateFormat = "YYYY-MM-DDTHH:mm:ssZ";
      const startMoment = moment(targetData.value.startDate).startOf("day");
      const formattedStartDate = startMoment.format(dateFormat);
      targetData.value.startDate = formattedStartDate;

      if (targetData.value.endDate) {
        const endMoment = moment(targetData.value.endDate).endOf("day");
        const formattedEndDate = endMoment.format(dateFormat);
        targetData.value.endDate = formattedEndDate;
      }
      if (targetData.value && targetData.value.status) {
        targetData.value.status = targetData.value.status.toUpperCase();
      }
      if (viewNameObj.value != "sites") {
        delete targetData.value.status;
        delete targetData.value.operationHours;
      }

      if (viewNameObj.value === "sites") {
        const week =
          operationalHoursEnabled.value && targetData.value.operationHours
            ? targetData.value.operationHours.week
            : {
                sun: { closed: false, open: "00:00", close: "23:59" },
                mon: { closed: false, open: "00:00", close: "23:59" },
                tue: { closed: false, open: "00:00", close: "23:59" },
                wed: { closed: false, open: "00:00", close: "23:59" },
                thu: { closed: false, open: "00:00", close: "23:59" },
                fri: { closed: false, open: "00:00", close: "23:59" },
                sat: { closed: false, open: "00:00", close: "23:59" },
              };

        const businessHoursObj: any = {
          businessHours: { week },
        };

        if (dataProp.value.edit) {
          businessHoursObj.siteId = targetData.value.webId;
        }

        targetData.value.siteBusinessHoursModels = [businessHoursObj];

        // Clean up
        delete targetData.value.operationHours;
      }
      emit("on-submit", targetData.value);
      if (viewNameObj.value != "sites") {
        formRef?.value?.resetForm();
      }
      // targetData.value["address"] = addressVal.value;
      addAddressField.value = true;
      loading.value = false;
    };
    const showOnForm = (element: IFormFieldModel): boolean => {
      return (
        element.showOnlyInEdit === formData.value.edit ||
        (element.showOnlyInEdit === false && formData.value.edit == true)
      );
    };
    const setFormValues = () => {
      nextTick(() => {
        if (!dataProp.value.edit) {
          targetData.value = {};
          formRef?.value?.resetForm();
          if (dataProp.value?.defaultValues) {
            targetData.value = { ...dataProp.value?.defaultValues };
          }
          if (viewNameObj.value === "sites") {
            const today = new Date();
            targetData.value["startDate"] = moment(today).format("YYYY-MM-DD");
            targetData.value["operationHours"] = { week: {} };
            daysOfWeek.forEach((day) => {
              targetData.value.operationHours.week[day.value] = {
                closed: false,
                open: "00:00",
                close: "23:59",
              };
            });
          }
        } else {
          targetData.value = { ...dataProp.value?.data };
          targetData.value.status =
            targetData.value.status.charAt(0).toUpperCase() +
            targetData.value.status.slice(1).toLowerCase();
          if (targetData.value["addressModel"])
            targetData.value["address"] =
              targetData.value["addressModel"]["street"] +
              ", " +
              targetData.value["addressModel"]["street2"] +
              "" +
              (targetData.value["addressModel"]["street2"] == "" ? "" : ", ") +
              targetData.value["addressModel"]["city"] +
              ", " +
              targetData.value["addressModel"]["state"] +
              ", " +
              targetData.value["addressModel"]["postalCode"] +
              ", " +
              targetData.value["addressModel"]["country"];

          if (targetData.value.startDate) {
            targetData.value.startDate = FormatMethods.convertUTCToLocalDate(
              targetData.value.startDate,
              "YYYY-MM-DD"
            );
          }
          if (targetData.value.endDate) {
            targetData.value.endDate = FormatMethods.convertUTCToLocalDate(
              targetData.value.endDate,
              "YYYY-MM-DD"
            );
          }

          let incomingWeek: any = null;
          if (targetData.value.businessHours?.week) {
            incomingWeek = cloneDeep(targetData.value.businessHours.week);
          } else {
            incomingWeek = {};
            daysOfWeek.forEach((day) => {
              incomingWeek[day.value] = {
                closed: false,
                open: "00:00",
                close: "23:59",
              };
            });
          }

          targetData.value.operationHours = {
            week: { ...incomingWeek },
          };

          // Normalize/clean up week data
          daysOfWeek.forEach((day) => {
            let dayData = targetData.value.operationHours.week[day.value];
            if (!dayData) {
              dayData = {
                closed: false,
                open: "00:00",
                close: "23:59",
              };
              targetData.value.operationHours.week[day.value] = dayData;
            } else {
              dayData.closed = !!dayData.closed;
              if (dayData.closed) {
                delete dayData.open;
                delete dayData.close;
              } else if (!dayData.open || !dayData.close) {
                dayData.open = "00:00";
                dayData.close = "23:59";
              }
            }
          });

          operationalHoursEnabled.value = true;
          delete targetData.value.businessHours;
        }

        formData.value = dataProp.value;
      });
    };
    watch(modelViewValue, (newValue) => {
      document.getElementsByClassName("modal-body")[0].scrollTop = 0;
      if (newValue) {
        setFormValues();
        emit("on-site-change", null);
        nextTick(() => {
          if (formScroll.value != null) {
            formScroll.value.scrollTop = 0;
          }
          const modalScrollContainers = document.querySelectorAll(
            ".scroll-y, .modal-body, [data-kt-scroll='true']"
          );
          modalScrollContainers.forEach((el: any) => {
            el.scrollTop = 0;
          });
        });
      } else {
        nextTick(() => {
          formRef?.value?.resetForm();
          if (formScroll.value != null) {
            formScroll.value.scrollTop = 0;
          }
        });
      }
    });
    watch(
      () => targetData.value.siteId,
      async (newVal, oldVal) => {
        const isInitial = oldVal === undefined;
        if (!(isInitial && dataProp.value.edit)) {
          emit("on-site-change", newVal);
        }
        if (!(isInitial && dataProp.value.edit)) {
          // Clear on any non-initial change
          targetData.value.locationId = "";
          targetData.value.locationName = "";
          if (targetData.value.locationRestriction) {
            targetData.value.locationRestriction = false;
          }
        }
      }
    );

    return {
      targetData,
      submit,
      discard,
      t,
      loading,
      formRef,
      formData,
      viewNameObj,
      newTargetModalRef,
      showOnForm,
      headerLabel,
      saveBtnLabel,
      isMulitSelectField,
      displayDate,
      dataProp,
      showAddModal,
      showAddressModal,
      updateShowModalVal,
      addressData,
      modelViewValue,
      addressSubmit,
      minDate,
      minEndDate,
      maskForElement,
      addressVal,
      isHeadQuarterStaff,
      addAddressField,
      validationOnUserEmail,
      // getCountries,
      // countryOptions,
      isEditMode,
      operationalHoursEnabled,
      toggleOperationalHours,
      daysOfWeek,
      onClosedChange,
      operationalHoursValidationError,
      getOperationalHoursOpenError,
      getOperationalHoursCloseError,
    };
  },
});
